import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  ScrollArea,
  Spinner,
  Text,
} from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import { PageHeader, PageHeaderSubtitle, PageHeaderTitle } from "#app/components/page-header";
import { SettingsGroup, SettingsGroupHeader, SettingsGroupRow } from "#app/components/settings/settings-group";
import {
  useIssueTypes,
  useIssueTypeMutations,
  type IssueTypeItem,
} from "#app/hooks/settings/use-issue-type-mutations";
import { IssueTypeCreateDialog } from "#app/components/settings/issue-type-create-dialog";
import { IssueTypeDeleteDialog } from "#app/components/settings/issue-type-delete-dialog";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { withPermission } from "#app/components/permissions/require-permission";

function IssueTypesPage() {
  const { push } = useAppNavigate();
  const { issueTypes, total, isLoading, error } = useIssueTypes();
  const { createIssueType, deleteIssueType, setDefaultIssueType } =
    useIssueTypeMutations();

  const [searchQuery, setSearchQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [deletingType, setDeletingType] = useState<IssueTypeItem | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const filteredTypes = useMemo(
    () =>
      issueTypes.filter((t) =>
        t.name.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [issueTypes, searchQuery],
  );

  async function handleCreate(data: { name: string; description: string }) {
    const result = await createIssueType({
      name: data.name,
      description: data.description || undefined,
      icon: { icon: "deployed_code", color: "dark-grey" },
    });
    if (result) {
      push(`/settings/issue-types/${result.id}`);
    }
  }

  async function handleDelete() {
    if (!deletingType) return;
    await deleteIssueType(deletingType.id, deletingType.issueTypeId);
  }

  async function handleSetDefault(issueType: IssueTypeItem) {
    await setDefaultIssueType(issueType.id);
  }

  if (isLoading && issueTypes.length === 0) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (error && issueTypes.length === 0) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Unable to load project types</EmptyTitle>
            <EmptyDescription>{error.message}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </Box>
    );
  }

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderTitle>Project types</PageHeaderTitle>
          <PageHeaderSubtitle>
            Project types define the structure for your work items. Customize your type requirements, allowing for default and required custom fields, specific automations and more.
          </PageHeaderSubtitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="xl" fill="width" maxWidth={800}>
            <InputGroup>
              <InputGroupAddon>
                <Icon name="search" />
              </InputGroupAddon>
              <InputGroupInput
                type="search"
                placeholder="Search types..."
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
            </InputGroup>

            <SettingsGroup>
              <SettingsGroupHeader>
                <Text size="sm" weight="medium">
                  {total} {total === 1 ? "type" : "types"}
                </Text>
                <Button
                  aria-label="Create project type"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setShowCreate(true)}
                >
                  <Icon name="plus" size="md" />
                </Button>
              </SettingsGroupHeader>

              {filteredTypes.length === 0 ? (
                <Box align="center" paddingY="xl">
                  <Text size="sm" variant="muted">
                    {searchQuery ? "No matching types" : "No project types yet"}
                  </Text>
                </Box>
              ) : (
                filteredTypes.map((issueType) => (
                  <SettingsGroupRow
                    key={issueType.issueTypeId}
                    label={`${issueType.name}${issueType.isDefault ? " · Default" : ""}`}
                    subtitle={issueType.description ?? undefined}
                    leading={
                      <EntityTag
                        entity={{
                          kind: "issueType",
                          id: issueType.issueTypeId,
                          issueType: {
                            name: issueType.name,
                            icon: {
                              icon: issueType.icon?.icon,
                              color: issueType.icon?.color,
                            },
                          },
                        }}
                        display="visual"
                        size="lg"
                        padded
                        interactive={false}
                      />
                    }
                    onClick={() => push(`/settings/issue-types/${issueType.id}`)}
                    actionsVisible={openMenuId === issueType.issueTypeId}
                  >
                    <Menu onOpenChange={(open) => setOpenMenuId(open ? issueType.issueTypeId : null)}>
                      <MenuTrigger
                        render={
                          <Button
                            aria-label="Project type actions"
                            variant="ghost"
                            size="icon-xs"
                          />
                        }
                      >
                        <Icon name="more-horizontal" size="md" />
                      </MenuTrigger>
                      <MenuPopup align="end" className="min-w-45">
                        <MenuItem onClick={() => handleSetDefault(issueType)} disabled={issueType.isDefault}>
                          <Icon name="check" size="md" />
                          Set as default
                        </MenuItem>
                        <MenuItem
                          variant="destructive"
                          onClick={() => setDeletingType(issueType)}
                          disabled={issueType.isDefault}
                        >
                          <Icon name="trash-2" size="md" />
                          Delete type...
                        </MenuItem>
                      </MenuPopup>
                    </Menu>
                  </SettingsGroupRow>
                ))
              )}
            </SettingsGroup>
          </Box>
        </Box>
      </ScrollArea>

      <IssueTypeCreateDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        onSubmit={handleCreate}
      />

      <IssueTypeDeleteDialog
        open={deletingType !== null}
        onOpenChange={(open) => { if (!open) setDeletingType(null); }}
        issueTypeName={deletingType?.name ?? ""}
        onConfirm={handleDelete}
      />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/issue-types/")({
  component: withPermission("issue_types.manage", IssueTypesPage),
});
